<script>
import LogisticsDefaultSuggestions from "@/components/fulfillment/LogisticsDefaultSuggestions.vue"
import LogisticsDefaults from "@/utils/logisticsDefaults"
import Title from "@/components/admin/common/TitleArea.vue"
import SpecTypeForm from "@/components/fulfillment/SpecTypeForm.vue"
import StatusBadge from "@/components/fulfillment/StatusBadge.vue"
import PageGuideDialog from "@/components/admin/fulfillment/guide/PageGuideDialog.vue"
import FulfillmentPageGuide from "@/components/admin/fulfillment/guide/FulfillmentPageGuide.vue"

export default {
  name: 'AdminMasterSkuDetail',
  components: {
    LogisticsDefaultSuggestions,
    Title,
    SpecTypeForm,
    StatusBadge,
    PageGuideDialog,
    FulfillmentPageGuide
  },
  data() {
    return {
      description: {
        title: '상품 상세'
      },
      masterSkuId: null,
      detail: null,
      saving: false,
      loading: false,
      unlinkingKey: '',
      approvingRequestId: null,
      guideDialog: false,
      masterDataLoading: false,
      brands: [],
      categories: [],
      packUnitTypes: [],
      form: {
        master_sku_code: '',
        barcode: '',
        display_name: '',
        brand_id: '',
        category_id: '',
        hs_code: '',
        spec_type: '',
        spec_detail: {},
        weight_kg: '',
        volume_cbm: '',
        length_cm: '',
        width_cm: '',
        height_cm: '',
        is_active: true,
      }
    }
  },
  methods: {
    applyDimensionDefaults(values) {
      Object.assign(values, LogisticsDefaults.applySuggestions(values))
    },
    onLogisticsValueInput(values, key) {
      Object.assign(values, LogisticsDefaults.clearAcceptance(values, key))
    },
    logisticsDefaultMetadata(values) {
      if (!Array.isArray(values.dimension_default_fields)) return {}
      return { dimension_default_fields: LogisticsDefaults.acceptedPayload(values).dimension_default_fields || [] }
    },

    goToList() {
      let query = {}
      try {
        query = JSON.parse(String(this.$route.query?.returnQuery || '{}'))
      } catch (_) {
        query = {}
      }
      this.$router.push({ path: '/admin/fulfillment/master-skus', query })
    },
    async loadMasterData() {
      this.masterDataLoading = true

      const brandConfig = {
        method: 'GET',
        url: '/api/fulfillment/brands',
        name: '[ Fulfillment / 브랜드 목록 ]'
      }
      const categoryConfig = {
        method: 'GET',
        url: '/api/fulfillment/categories',
        name: '[ Fulfillment / 카테고리 목록 ]'
      }
      const packUnitConfig = {
        method: 'GET',
        url: '/api/fulfillment/pack-unit-types',
        name: '[ Fulfillment / 패키지 단위 목록 ]'
      }

      await this.$http(brandConfig)
        .then(res => { this.brands = res.data?.data || [] })
        .catch(() => { this.brands = [] })

      await this.$http(categoryConfig)
        .then(res => { this.categories = res.data?.data || [] })
        .catch(() => { this.categories = [] })

      await this.$http(packUnitConfig)
        .then(res => { this.packUnitTypes = res.data?.data || [] })
        .catch(() => { this.packUnitTypes = [] })

      this.masterDataLoading = false
    },
    notify(message, type = 'negative') {
      if (this.$q && this.$q.notify) {
        this.$q.notify({ message, type })
        return
      }
      alert(message)
    },
    normalizeSpecDetailForUi(specType, specDetail = {}) {
      const type = String(specType || '').toUpperCase()
      if (type === 'DIMENSION') {
        return {
          length_cm: specDetail.length_cm ?? specDetail.length ?? '',
          width_cm: specDetail.width_cm ?? specDetail.width ?? '',
          height_cm: specDetail.height_cm ?? specDetail.height ?? '',
        }
      }
      if (type === 'VOLUME') {
        return {
          volume_value: specDetail.volume_value ?? specDetail.value ?? '',
          volume_unit: specDetail.volume_unit ?? specDetail.unit ?? 'ml',
        }
      }
      if (type === 'APPAREL_SIZE') {
        return {
          apparel_size: specDetail.apparel_size ?? specDetail.size ?? '',
        }
      }
      if (type === 'PRODUCT_OPTION') {
        return {
          model_code: specDetail.model_code ?? specDetail.model ?? '',
          color: specDetail.color ?? '',
          size: specDetail.size ?? specDetail.size_value ?? '',
        }
      }
      if (type === 'PACK_COUNT') {
        return {
          pack_unit_type_code: specDetail.pack_unit_type_code ?? specDetail.unit ?? '',
          pack_count: specDetail.pack_count ?? specDetail.count ?? '',
          pack_uom: specDetail.pack_uom ?? 'ea',
        }
      }
      return {}
    },
    async loadDetail() {
      if (!this.masterSkuId) return
      this.loading = true
      const config = {
        method: 'GET',
        url: `/api/fulfillment/admin/master-skus/${this.masterSkuId}`,
        name: '[ Fulfillment / Master SKU 상세 ]'
      }
      await this.$http(config)
        .then(res => {
          this.detail = res.data?.data
          this.form = {
            master_sku_code: this.detail.master_sku_code || '',
            barcode: this.detail.barcode || '',
            display_name: this.detail.display_name || '',
            brand_id: this.detail.brand_id || '',
            category_id: this.detail.category_id || '',
            hs_code: this.detail.hs_code || '',
            spec_type: this.detail.spec_type || '',
            spec_detail: this.normalizeSpecDetailForUi(this.detail.spec_type, this.detail.spec_detail || {}),
            weight_kg: this.detail.weight_kg ?? '',
            volume_cbm: this.detail.volume_cbm ?? '',
            length_cm: this.detail.length_cm ?? '',
            width_cm: this.detail.width_cm ?? '',
            height_cm: this.detail.height_cm ?? '',
            is_active: this.detail.is_active !== false,
          }
        })
        .catch(() => {
          this.detail = null
        })
      this.loading = false
    },
    async save() {
      if (!this.masterSkuId) return
      const spec_key = this.$makeSpecKey(this.form.spec_type, this.form.spec_detail)
      if (!spec_key) {
        this.notify('spec_detail을 확인하세요.')
        return
      }
      if (!String(this.form.master_sku_code || '').trim()) {
        this.notify('마스터 SKU를 입력하세요.')
        return
      }
      this.saving = true
      const config = {
        method: 'PUT',
        url: `/api/fulfillment/admin/master-skus/${this.masterSkuId}`,
        data: {
          master_sku_code: String(this.form.master_sku_code || '').trim(),
          barcode: this.form.barcode || null,
          display_name: this.form.display_name,
          brand_id: this.form.brand_id,
          category_id: this.form.category_id,
          hs_code: this.form.hs_code,
          spec_type: this.form.spec_type,
          spec_key,
          spec_detail: this.form.spec_detail,
          ...this.logisticsDefaultMetadata(this.form),
          weight_kg: this.form.weight_kg,
          volume_cbm: this.form.volume_cbm,
          length_cm: this.form.length_cm,
          width_cm: this.form.width_cm,
          height_cm: this.form.height_cm,
          is_active: this.form.is_active,
        },
        name: '[ Fulfillment / Master SKU 수정 ]'
      }
      await this.$http(config)
        .then(() => {
          this.notify('저장되었습니다.', 'positive')
          this.loadDetail()
        })
        .catch(() => {
          this.notify('저장에 실패했습니다.')
        })
      this.saving = false
    },
    async toggleActive() {
      if (!this.masterSkuId) return
      const config = {
        method: 'PATCH',
        url: `/api/fulfillment/admin/master-skus/${this.masterSkuId}/active`,
        data: { is_active: this.form.is_active },
        name: '[ Fulfillment / Master SKU 활성 상태 변경 ]'
      }
      await this.$http(config)
        .then(() => {
          this.notify('상태가 변경되었습니다.', 'positive')
        })
        .catch(() => {
          this.notify('상태 변경에 실패했습니다.')
        })
    },
    openApproval(requestId) {
      if (!requestId) return
      this.$router.push(`/admin/fulfillment/approvals/${requestId}`)
    },
    async unlinkPartnerSku(item) {
      if (!this.masterSkuId || !item?.partner_id || !item?.partner_sku_code) return
      const agreed = window.confirm(`파트너 SKU 연결을 해제하시겠습니까?\n${item.partner_name || item.partner_id} / ${item.partner_sku_code}`)
      if (!agreed) return

      const key = `${item.partner_id}-${item.partner_sku_code}`
      this.unlinkingKey = key
      const config = {
        method: 'POST',
        url: `/api/fulfillment/admin/master-skus/${this.masterSkuId}/partner-skus/unlink`,
        data: {
          partner_id: item.partner_id,
          partner_sku_code: item.partner_sku_code,
        },
        name: '[ Fulfillment / Partner SKU 연결 해제 ]'
      }

      await this.$http(config)
        .then(() => {
          this.notify('연결이 해제되었습니다.', 'positive')
          this.loadDetail()
        })
        .catch((error) => {
          this.notify(error?.response?.data?.message || '연결 해제에 실패했습니다.')
        })
      this.unlinkingKey = ''
    },
    async approveRequestToCurrentProduct(item) {
      if (!this.masterSkuId || !item?.request_id) return
      const agreed = window.confirm(`이 요청을 현재 상품에 바로 연결 승인하시겠습니까?\n요청 #${item.request_id} / ${item.partner_sku_code}`)
      if (!agreed) return

      this.approvingRequestId = item.request_id
      const config = {
        method: 'POST',
        url: `/api/fulfillment/admin/sku-requests/${item.request_id}/approve/link`,
        data: { master_sku_id: Number(this.masterSkuId) },
        name: '[ Fulfillment / 상품 상세 즉시 연결 승인 ]'
      }

      await this.$http(config)
        .then(() => {
          this.notify('현재 상품으로 연결 승인되었습니다.', 'positive')
          this.loadDetail()
        })
        .catch((error) => {
          this.notify(error?.response?.data?.message || '연결 승인에 실패했습니다.')
        })
      this.approvingRequestId = null
    },
    sanitizeBarcode(value) {
      this.form.barcode = String(value || '').replace(/\D+/g, '').slice(0, 64)
    },
    normalizeMasterSkuCode() {
      this.form.master_sku_code = String(this.form.master_sku_code || '').trim().toUpperCase()
    }
  },
  mounted() {
    this.masterSkuId = this.$route.params.id
    this.loadMasterData()
    this.loadDetail()
  }
}
</script>

<template>
  <q-page class="q-pa-lg">
    <div class="row q-mb-lg">
      <div class="col">
        <Title :desc="description">
          <template #title-action>
            <q-btn flat round dense size="sm" color="primary" icon="help_outline" aria-label="상품 상세 사용 가이드" @click="guideDialog = true">
              <q-tooltip>상품 상세 사용 가이드</q-tooltip>
            </q-btn>
          </template>
          <q-btn flat color="grey-7" icon="arrow_back" label="목록으로" @click="goToList" />
          <q-btn
            color="primary"
            icon="save"
            label="저장"
            class="q-ml-sm"
            :loading="saving"
            @click="save"
          />
        </Title>
      </div>
    </div>

    <div class="row q-mb-md">
      <div class="col">
        <div class="comp-box q-pa-md">
          <div class="row items-start justify-between q-col-gutter-md">
            <div class="col">
              <div class="text-subtitle1 text-weight-bold">상품 기본 정보</div>
              <div class="text-body2 text-grey-7 q-mt-xs">마스터 SKU의 기본 정보와 규격, 활성 상태를 관리합니다.</div>
            </div>
          </div>
          <div v-if="detail" class="row q-col-gutter-md q-mt-sm q-mb-md">
            <div class="col-12 col-md-3">
              <div class="text-caption text-grey">마스터 SKU</div>
              <div class="text-body2">{{ detail.master_sku_code }}</div>
            </div>
            <div class="col-12 col-md-3">
              <div class="text-caption text-grey">바코드번호</div>
              <div class="text-body2">{{ detail.barcode || '-' }}</div>
            </div>
            <div class="col-12 col-md-3">
              <div class="text-caption text-grey">연결 상태</div>
              <StatusBadge :status="detail.mapping_status" />
            </div>
            <div class="col-12 col-md-3">
              <div class="text-caption text-grey">등록상태</div>
              <StatusBadge :status="detail.registration_status || 'CONFIRMED'" />
            </div>
            <div class="col-12 col-md-3">
              <div class="text-caption text-grey">파트너 SKU 수</div>
              <div class="text-body2">{{ detail.partner_sku_count || 0 }}</div>
            </div>
            <div class="col-12 col-md-3">
              <div class="text-caption text-grey">활성 상태</div>
              <div class="row items-center q-gutter-sm no-wrap">
                <StatusBadge :status="form.is_active ? 'ACTIVE' : 'INACTIVE'" />
                <q-toggle
                  v-model="form.is_active"
                  dense
                  color="green"
                  @update:model-value="toggleActive"
                />
              </div>
            </div>
          </div>
          <div v-if="detail" class="sku-form-sections q-mt-sm">
            <section class="sku-form-section">
              <div class="sku-form-section__header">
                <div class="sku-form-section__title">식별 정보</div>
                <div class="sku-form-section__caption">상품을 구분하고 바코드 기준으로 마스터 SKU를 관리합니다.</div>
              </div>
              <div class="row q-col-gutter-md">
                <div class="col-12 col-md-4">
                  <q-input v-model="form.display_name" label="상품명" dense outlined />
                </div>
                <div class="col-12 col-md-4">
                  <q-input
                    v-model="form.barcode"
                    label="바코드번호"
                    dense
                    outlined
                    maxlength="64"
                    inputmode="numeric"
                    @update:model-value="sanitizeBarcode"
                  />
                </div>
                <div class="col-12 col-md-4">
                  <q-input
                    v-model="form.master_sku_code"
                    label="마스터 SKU"
                    dense
                    outlined
                    maxlength="32"
                    @blur="normalizeMasterSkuCode"
                  />
                </div>
              </div>
            </section>

            <section class="sku-form-section">
              <div class="sku-form-section__header">
                <div class="sku-form-section__title">분류 정보</div>
                <div class="sku-form-section__caption">동일 SKU 판단에 사용하는 브랜드, 카테고리, HS CODE, 규격 타입입니다.</div>
              </div>
              <div class="row q-col-gutter-md">
                <div class="col-12 col-md-4">
                  <q-select
                    v-model="form.brand_id"
                    :options="brands"
                    option-label="name"
                    option-value="brand_id"
                    emit-value
                    map-options
                    label="브랜드"
                    dense
                    outlined
                  />
                </div>
                <div class="col-12 col-md-4">
                  <q-select
                    v-model="form.category_id"
                    :options="categories"
                    option-label="name"
                    option-value="category_id"
                    emit-value
                    map-options
                    label="카테고리"
                    dense
                    outlined
                  />
                </div>
                <div class="col-12 col-md-4">
                  <q-input v-model="form.hs_code" label="HS CODE" dense outlined />
                </div>
              </div>
            </section>

            <section class="sku-form-section">
              <div class="sku-form-section__header">
                <div class="sku-form-section__title">물류 기본값</div>
                <div class="sku-form-section__caption">중량, 부피, 기본 치수처럼 포장과 운영에 쓰는 값입니다.</div>
              </div>
              <div class="row q-col-gutter-md items-center">
                <div class="col-12 col-md-2">
                  <q-input v-model="form.weight_kg" @update:model-value="onLogisticsValueInput(form, 'weight_kg')" label="중량 (kg)" dense outlined />
                </div>
                <div class="col-12 col-md-2">
                  <q-input v-model="form.volume_cbm" @update:model-value="onLogisticsValueInput(form, 'volume_cbm')" label="부피 (cbm)" dense outlined />
                </div>
                <div class="col-12 col-md-2">
                  <q-input v-model="form.length_cm" label="가로 (cm)" dense outlined />
                </div>
                <div class="col-12 col-md-2">
                  <q-input v-model="form.width_cm" label="세로 (cm)" dense outlined />
                </div>
                <div class="col-12 col-md-2">
                  <q-input v-model="form.height_cm" label="높이 (cm)" dense outlined />
                </div>
              </div>
              <LogisticsDefaultSuggestions class="q-mt-sm" :values="form" :disabled="saving" @apply="applyDimensionDefaults(form)" />
            </section>

            <section class="sku-form-section">
              <div class="sku-form-section__header">
                <div class="sku-form-section__title">규격 상세</div>
                <div class="sku-form-section__caption">상품 옵션, 의류 사이즈, 포장 입수량처럼 동일 SKU를 나누는 값입니다.</div>
              </div>
              <div class="row q-col-gutter-md">
                <div class="col-12 col-md-4">
                  <q-select
                    v-model="form.spec_type"
                    :options="[
                      { label: 'APPAREL_SIZE', value: 'APPAREL_SIZE' },
                      { label: 'PRODUCT_OPTION', value: 'PRODUCT_OPTION' },
                      { label: 'PACK_COUNT', value: 'PACK_COUNT' },
                    ]"
                    emit-value
                    map-options
                    label="규격 타입"
                    dense
                    outlined
                  />
                </div>
                <div class="col-12">
                  <SpecTypeForm
                    :spec-type="form.spec_type"
                    v-model="form.spec_detail"
                  />
                </div>
              </div>
            </section>
          </div>
          <div v-else class="text-caption text-grey">상세 정보를 불러오는 중...</div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12 q-mb-md">
        <div class="comp-box q-pa-md">
          <div class="row items-center q-mb-sm">
            <div class="text-subtitle1">파트너 SKU 연결 현황</div>
            <q-badge color="grey-7" outline class="q-ml-sm">
              {{ detail?.partner_skus?.length || 0 }}건
            </q-badge>
          </div>
          <div class="text-caption text-grey-7 q-mb-md">
            현재 이 상품에 실제 연결된 파트너 SKU입니다. 연결된 row가 있을 때만 `연결 해제` 버튼이 표시됩니다.
          </div>
          <q-markup-table flat bordered separator="cell">
            <thead>
              <tr>
                <th class="text-left">파트너 ID</th>
                <th class="text-left">파트너명</th>
                <th class="text-left">파트너 SKU</th>
                <th class="text-left">바코드</th>
                <th class="text-center">액션</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!(detail?.partner_skus || []).length">
                <td colspan="5" class="text-center text-grey">연결된 파트너 SKU가 없습니다.</td>
              </tr>
              <tr v-for="item in (detail?.partner_skus || [])" :key="`${item.partner_id}-${item.partner_sku_code}`">
                <td>{{ item.partner_id }}</td>
                <td>{{ item.partner_name || '-' }}</td>
                <td>{{ item.partner_sku_code }}</td>
                <td>{{ item.barcode || '-' }}</td>
                <td class="text-center">
                  <q-btn
                    unelevated
                    size="sm"
                    color="negative"
                    icon="link_off"
                    label="연결 해제"
                    :loading="unlinkingKey === `${item.partner_id}-${item.partner_sku_code}`"
                    @click="unlinkPartnerSku(item)"
                  />
                </td>
              </tr>
            </tbody>
          </q-markup-table>
        </div>
      </div>
      <div class="col-12 q-mb-md">
        <div class="comp-box q-pa-md">
          <div class="row items-center q-mb-sm">
            <div class="text-subtitle1">관련 요청 현황</div>
            <q-badge color="grey-7" outline class="q-ml-sm">
              {{ detail?.related_requests?.length || 0 }}건
            </q-badge>
          </div>
          <q-markup-table flat bordered separator="cell">
            <thead>
              <tr>
                <th class="text-left">요청 ID</th>
                <th class="text-left">파트너</th>
                <th class="text-left">파트너 SKU</th>
                <th class="text-left">바코드</th>
                <th class="text-center">상태</th>
                <th class="text-left">최근 변경</th>
                <th class="text-center">액션</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!(detail?.related_requests || []).length">
                <td colspan="7" class="text-center text-grey">이 상품 기준의 관련 요청이 없습니다.</td>
              </tr>
              <tr v-for="item in (detail?.related_requests || [])" :key="item.request_id">
                <td>{{ item.request_id }}</td>
                <td>{{ item.partner_name || `#${item.partner_id}` }}</td>
                <td>{{ item.partner_sku_code }}</td>
                <td>{{ item.barcode || '-' }}</td>
                <td class="text-center">
                  <StatusBadge :status="item.status === 'REQUESTED' ? 'PENDING' : 'REJECTED'" />
                </td>
                <td>{{ item.updated_at || item.created_at || '-' }}</td>
                <td class="text-center">
                  <div class="row justify-center q-gutter-xs">
                    <q-btn
                      v-if="item.status === 'REQUESTED'"
                      unelevated
                      size="sm"
                      color="positive"
                      icon="check_circle"
                      label="바로 연결 승인"
                      :loading="approvingRequestId === item.request_id"
                      @click="approveRequestToCurrentProduct(item)"
                    />
                    <q-btn
                      flat
                      dense
                      color="primary"
                      label="요청 보기"
                      @click="openApproval(item.request_id)"
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </q-markup-table>
        </div>
      </div>
    </div>

    <PageGuideDialog
      v-model="guideDialog"
      title="상품 상세 사용 가이드"
      description="기준 상품 수정과 파트너 SKU 연결 관리 방법입니다."
    >
      <FulfillmentPageGuide guide-key="masterSkuDetail" />
    </PageGuideDialog>
  </q-page>
</template>

<style scoped>
.sku-form-sections {
  border: 1px solid #dfe5ec;
  border-radius: 10px;
  background: #fff;
}

.sku-form-section {
  padding: 18px;
}

.sku-form-section + .sku-form-section {
  border-top: 1px solid #e7ecf2;
}

.sku-form-section__header {
  margin-bottom: 14px;
}

.sku-form-section__title {
  color: #263238;
  font-size: 15px;
  font-weight: 700;
}

.sku-form-section__caption {
  margin-top: 3px;
  color: #6b7280;
  font-size: 12px;
  line-height: 1.45;
}
</style>
